// Categories.jsx
import React, { useEffect, useState, useRef } from "react";
import { supabase } from "../../supabase/supabase";
import { useLanguage } from "../../contexts/LanguageContext";
import "./Categories.css";

// Helper function to resolve category logo URL from Supabase Storage
const resolveCategoryLogoUrl = (filename) => {
  if (!filename) return null;
  
  // If already a full URL, return it
  if (typeof filename === 'string' && /^(http|https):\/\//i.test(filename)) {
    return filename;
  }
  
  // If data URL, return it
  if (typeof filename === 'string' && filename.startsWith('data:')) {
    return filename;
  }
  
  // Construct URL from Supabase Storage categories bucket
  try {
    const { data } = supabase.storage.from('categories').getPublicUrl(filename);
    return data?.publicUrl || null;
  } catch (e) {
    console.log('[Categories] Error resolving logo URL:', e);
    return null;
  }
};

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const wrapperRef = useRef(null);
  const categoryRefs = useRef({});
  const containerRef = useRef(null);
  const isDown = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  // ⬇️ get current language and translations
  const { translations: t, lang, language, locale } = useLanguage();
  const currentLang = (lang || language || locale || t?.lang || t?.locale || "en")
    .toString()
    .toLowerCase();
  const isArabic = currentLang.startsWith("ar");

  // 1️⃣ Fetch
  useEffect(() => {
    (async () => {
      try {
        // Fetch categories from account_categories table in Supabase
        const { data: categoriesData, error } = await supabase
          .from('account_categories')
          .select('*');

        if (error) {
          console.error('[Categories] Error fetching categories:', error);
          return;
        }

        // Transform and sort categories; use order column when present
        const transformedCategories = (categoriesData || [])
          .map(cat => {
            const logoFilename =
              cat.image_filename ||
              cat.logo ||
              cat.logo_filename ||
              cat.image ||
              `${cat.name}.png`;

            return {
              id: cat.id,
              name: cat.name,
              name_ar: cat.name_ar || cat.name,
              sort_order: typeof cat.sort_order === 'number' ? cat.sort_order : null,
              logo: resolveCategoryLogoUrl(logoFilename)
            };
          })
          .sort((a, b) => {
            const sortOrderA = a.sort_order ?? 0;
            const sortOrderB = b.sort_order ?? 0;
            if (sortOrderA !== sortOrderB) return sortOrderA - sortOrderB;
            return (a.name || '').localeCompare(b.name || '');
          });

        setCategories(transformedCategories);
      } catch (err) {
        console.error('[Categories] Error loading categories:', err);
      }
    })();
  }, []);

  // 2️⃣ Drag handlers
  const handleMouseDown = e => {
    isDown.current = true;
    wrapperRef.current.classList.add("dragging");
    startX.current = e.pageX - wrapperRef.current.offsetLeft;
    scrollLeft.current = wrapperRef.current.scrollLeft;
  };
  const handleMouseMove = e => {
    if (!isDown.current) return;
    e.preventDefault();
    const x = e.pageX - wrapperRef.current.offsetLeft;
    wrapperRef.current.scrollLeft = scrollLeft.current - (x - startX.current);
  };
  const handleMouseUp = () => {
    if (!wrapperRef.current) return;
    isDown.current = false;
    wrapperRef.current.classList.remove("dragging");
    const w = wrapperRef.current;
    if (w.scrollLeft <= 0) w.classList.add("bounce-right");
    else if (w.scrollLeft + w.clientWidth >= w.scrollWidth) w.classList.add("bounce-left");
  };
  const handleMouseLeave = handleMouseUp;
  const handleTouchStart = e => {
    isDown.current = true;
    startX.current = e.touches[0].pageX - wrapperRef.current.offsetLeft;
    scrollLeft.current = wrapperRef.current.scrollLeft;
  };
  const handleTouchMove = e => {
    if (!isDown.current) return;
    const x = e.touches[0].pageX - wrapperRef.current.offsetLeft;
    wrapperRef.current.scrollLeft = scrollLeft.current - (x - startX.current);
  };
  const handleTouchEnd = handleMouseUp;

  // 3️⃣ Remove bounce classes
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const clean = e => {
      if (e.animationName === "bounceLeft" || e.animationName === "bounceRight")
        el.classList.remove("bounce-left", "bounce-right");
    };
    el.addEventListener("animationend", clean);
    return () => el.removeEventListener("animationend", clean);
  }, []);

  // 4️⃣ Outside‐click closes
  useEffect(() => {
    const onOutside = e => {
      if (!containerRef.current?.contains(e.target) && !wrapperRef.current?.contains(e.target))
        setSelectedCategory(null);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  // 5️⃣ Toggle popup
  const handleCategoryClick = (catId) => {
    const existingRipple = document.querySelector('.liquid-ripple');
    if (existingRipple) existingRipple.remove();

    if (selectedCategory === catId) {
      setSelectedCategory(null);
      return;
    }

    const item = categoryRefs.current[catId];
    if (item) {
      const ripple = document.createElement('div');
      ripple.className = 'liquid-ripple';
      item.appendChild(ripple);
      setTimeout(() => ripple.remove(), 1000);

      item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    setSelectedCategory(catId);
  };

  const descriptions = {
    Apps: t.categories?.descriptions?.apps || "For tech entrepreneurs and developers to showcase their digital products and connect with users.",
    "Beauty & Care": t.categories?.descriptions?.beauty || "Targeted at salons and care centers. Supports listing beauty services, and available products",
    Business: t.categories?.descriptions?.business || "Designed for companies to showcase commercial activities and services within the app.",
    Coffee: t.categories?.descriptions?.coffee || "Everything coffee—from brewing guides to café reviews and bean origins.",
    Cars: t.categories?.descriptions?.cars || "For car showrooms and services like rental or maintenance.Supports detailed vehicule displays.",
    Community: t.categories?.descriptions?.community || "Supports local initiatives and social activities, offering a space to showcase programs and contributions.",
    Delivery: t.categories?.descriptions?.delivery || "For logistics services. Supports efficient order tracking and delivery scheduling.",
    Education: t.categories?.descriptions?.education || "Displays educational programs, training courses and registrations mechanisms.",
    Entertainment: t.categories?.descriptions?.entertainment || "Allows content creators to showcase entertainment activities and offers with organized, bookable data",
    Finance: t.categories?.descriptions?.finance || "Dedicated to electronic payment services, money transfers, and digital wallets with banking integration",
    Food: t.categories?.descriptions?.food || "Allow Restaurants to display menus,set working hours and communicate directly with customers.",
    Freelance: t.categories?.descriptions?.freelance || "For freelancers to present skills and services and receive job requests directly through the app",
    Government: t.categories?.descriptions?.government || "An Official interface for government entities to present public services and connect with citizens.",
    Health: t.categories?.descriptions?.health || "Designed for healthcare entities. Allow showcasing clinics info , medications and medical services",
    Hotels: t.categories?.descriptions?.hotels || "Hotel reviews,booking hacks and destination guides for your next stay.",
    Jewellery: t.categories?.descriptions?.jewellery || "Trends,care tips and designer insights on all things jewellery and gems.",
    Jobs: t.categories?.descriptions?.jobs || "Dedicated to posting job vacancies and receiving CVs in an organized format.",
    Markets: t.categories?.descriptions?.markets || "Tailored for speciality stores.Supports products listings,categories, and pricing in unified interface",
    Massage: t.categories?.descriptions?.massage || "Enable Massage centers to present service types, schedualing and contact points",
    Models: t.categories?.descriptions?.models || "Technology to display model portfolios and accept requests for photography and professional collaborations",
    Pets: t.categories?.descriptions?.pets || "For pet shops and related products, offering visual display and engagement options.",
    "Real Estate": t.categories?.descriptions?.realEstate || "Listings, market trends and home-buying tips for buyers and renters.",
    Services: t.categories?.descriptions?.services || "Covers all operational service providers like maintenance and transport , with scheduling capabilities",
    Sports: t.categories?.descriptions?.sports || "For sports Facilities. Enables displaying training sessions, subscriptions, and available services",
    "Stuff & More": t.categories?.descriptions?.stuff || "Flexible category for showcasing products or services that don't fit into other classifications",
    "Taxi+": t.categories?.descriptions?.taxi || "For transportation service providers. Connects the provider with users to fulfill booking requests.",
    Travel: t.categories?.descriptions?.travel || "Used by travel agencies to organize trip data and bookings in a structured manner."
  };

  return (
    <section className="categories-section" id="categories" ref={containerRef}>
      <div className="category-header">
        <h2 className="category-title">{t.categories?.header?.title || "Explore by Category"}</h2>
        <p className="category-subtitle">
          {t.categories?.header?.subtitle || "Navigate through a variety of categories tailored to fit your interests and personal needs."}
        </p>
      </div>

      <div
        className="category-marquee-wrapper"
        ref={wrapperRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className={`category-marquee ${selectedCategory ? 'has-popup' : ''}`}>
          {categories.map(cat => {
            const displayName = isArabic ? (cat.name_ar || cat.name) : cat.name; // ← choose Arabic when applicable

            return (
              <div
                key={cat.id}
                ref={el => (categoryRefs.current[cat.id] = el)}
                className={`category-item ${selectedCategory === cat.id ? "selected" : ""}`}
                onClick={() => handleCategoryClick(cat.id)}
                title={displayName}                 // browser tooltip in correct language
              >
                <div className="category-logo">
                  <img 
                    src={cat.logo || "/default-category.png"} 
                    alt={displayName}
                    onError={(e) => {
                      e.target.src = "/default-category.png";
                    }}
                  />
                </div>

                {/* visible label under icon */}
                <div className="category-name" dir={isArabic ? "rtl" : "ltr"}>
                  {displayName}
                </div>

                {selectedCategory === cat.id && (
                  <div className="category-popup" dir={isArabic ? "rtl" : "ltr"}>
                    {descriptions[cat.name] || (isArabic ? "لا يوجد وصف." : "No description available.")}
                    <div className="tooltip-pointer"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Categories;
